import { useEffect, useState } from "react";
import { Barcode, Camera, Check, LoaderCircle, Minus, PackagePlus, Plus, X } from "lucide-react";
import { lookupBarcode, searchProducts } from "../api";
import { adjustNonNegative } from "../lib/stock";
import type { InventoryInput, InventoryItem, PantryLocation, ProductHistoryResult, ProductLookup, ShoppingItem } from "../types";
import { BarcodeScanner } from "./BarcodeScanner";

type Props = {
  initial?: InventoryItem | null;
  shoppingItem?: ShoppingItem | null;
  onSave: (input: InventoryInput, existing?: InventoryItem | null) => Promise<void>;
  onClose: () => void;
};

const defaultInput: InventoryInput = {
  productName: "",
  brand: "",
  barcode: "",
  category: "Other",
  quantity: 1,
  unit: "pcs",
  isOpen: false,
  location: "Pantry",
  expiryDate: "",
  expiryType: "best-before",
  minimumQuantity: 0,
  notes: "",
  imageUrl: "",
};

const historyStatusLabels: Record<ProductHistoryResult["status"], string> = {
  ACTIVE: "In pantry",
  CONSUMED: "Used",
  DISCARDED: "Discarded",
  DELETED: "Deleted",
};

function NumberControl({ id, label, value, onChange }: {
  id: string;
  label: string;
  value: number;
  onChange: (value: number) => void;
}) {
  return (
    <div className="number-control">
      <button type="button" onClick={() => onChange(adjustNonNegative(value, -1))} disabled={value <= 0} aria-label={`Decrease ${label}`}><Minus /></button>
      <input id={id} type="number" inputMode="decimal" min="0" step="0.1" value={value} onChange={(event) => onChange(Number(event.target.value))} />
      <button type="button" onClick={() => onChange(adjustNonNegative(value, 1))} aria-label={`Increase ${label}`}><Plus /></button>
    </div>
  );
}

export function ItemForm({ initial, shoppingItem, onSave, onClose }: Props) {
  const [form, setForm] = useState<InventoryInput>(() => initial ? {
    productName: initial.productName, brand: initial.brand ?? "", barcode: initial.barcode ?? "",
    category: initial.category, quantity: initial.quantity, unit: initial.unit, isOpen: initial.isOpen ?? false, location: initial.location,
    expiryDate: initial.expiryDate ?? "", expiryType: initial.expiryType, minimumQuantity: initial.minimumQuantity,
    notes: initial.notes ?? "", imageUrl: initial.imageUrl ?? "",
  } : { ...defaultInput, productName: shoppingItem?.name ?? "", quantity: shoppingItem?.quantity ?? 1, unit: shoppingItem?.unit ?? "pcs" });
  const [scannerOpen, setScannerOpen] = useState(false);
  const [lookup, setLookup] = useState<ProductLookup | null>(null);
  const [lookupBusy, setLookupBusy] = useState(false);
  const [historyResults, setHistoryResults] = useState<ProductHistoryResult[]>([]);
  const [historyBusy, setHistoryBusy] = useState(false);
  const [historyError, setHistoryError] = useState("");
  const [historySelection, setHistorySelection] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const scrollY = window.scrollY;
    const previous = {
      position: document.body.style.position,
      top: document.body.style.top,
      left: document.body.style.left,
      right: document.body.style.right,
      width: document.body.style.width,
      overflow: document.body.style.overflow,
    };
    document.body.classList.add("modal-open");
    Object.assign(document.body.style, {
      position: "fixed",
      top: `-${scrollY}px`,
      left: "0",
      right: "0",
      width: "100%",
      overflow: "hidden",
    });
    return () => {
      document.body.classList.remove("modal-open");
      Object.assign(document.body.style, previous);
      window.scrollTo(0, scrollY);
    };
  }, []);

  useEffect(() => {
    if (initial) return;
    const query = form.productName.trim();
    if (query.length < 2 || query.toLowerCase() === historySelection) {
      setHistoryResults([]);
      setHistoryBusy(false);
      setHistoryError("");
      return;
    }

    let cancelled = false;
    setHistoryBusy(true);
    setHistoryError("");
    const timer = window.setTimeout(async () => {
      try {
        const results = await searchProducts(query);
        if (!cancelled) setHistoryResults(results);
      } catch {
        if (!cancelled) {
          setHistoryResults([]);
          setHistoryError("Could not search saved household products.");
        }
      } finally {
        if (!cancelled) setHistoryBusy(false);
      }
    }, 250);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [form.productName, historySelection, initial]);

  function change<K extends keyof InventoryInput>(key: K, value: InventoryInput[K]) {
    setForm((current) => ({ ...current, [key]: value }));
  }

  function closeForm() {
    if (document.activeElement instanceof HTMLElement) document.activeElement.blur();
    window.requestAnimationFrame(onClose);
  }

  function selectHistoryProduct(product: ProductHistoryResult) {
    setHistorySelection(product.productName.trim().toLowerCase());
    setHistoryResults([]);
    setHistoryError("");
    setForm((current) => ({
      ...current,
      productName: product.productName,
      brand: product.brand ?? "",
      barcode: product.barcode ?? "",
      category: product.category || "Other",
      unit: product.unit || current.unit,
      isOpen: false,
      location: product.location || current.location,
      expiryDate: "",
      expiryType: product.expiryType || current.expiryType,
      minimumQuantity: product.minimumQuantity ?? current.minimumQuantity,
      notes: product.notes ?? "",
      imageUrl: product.imageUrl ?? "",
    }));
  }

  async function resolveBarcode(barcode: string) {
    setScannerOpen(false);
    setLookupBusy(true);
    setError("");
    change("barcode", barcode);
    try {
      const product = await lookupBarcode(barcode);
      setLookup(product);
      if (product.found) {
        setForm((current) => ({
          ...current,
          barcode,
          productName: product.productName || current.productName,
          brand: product.brand || current.brand,
          category: product.category || current.category,
          imageUrl: product.imageUrl || current.imageUrl,
        }));
      }
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Product lookup failed.");
    } finally {
      setLookupBusy(false);
    }
  }

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    if (!form.productName.trim()) return setError("Add a product name.");
    if (form.quantity < 0) return setError("Quantity cannot be negative.");
    if (document.activeElement instanceof HTMLElement) document.activeElement.blur();
    setSaving(true);
    setError("");
    try {
      await onSave({ ...form, productName: form.productName.trim(), brand: form.brand?.trim(), barcode: form.barcode?.trim() }, initial);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not save this item.");
      setSaving(false);
    }
  }

  return (
    <>
      <div className="sheet-backdrop" onMouseDown={(event) => event.target === event.currentTarget && closeForm()}>
        <section className="item-sheet" role="dialog" aria-modal="true" aria-labelledby="item-form-title">
          <header className="sheet-header">
            <div>
              <span className="eyebrow">{initial ? "UPDATE STOCK" : shoppingItem ? "MOVE TO PANTRY" : "NEW PANTRY ITEM"}</span>
              <h2 id="item-form-title">{initial ? "Edit item" : "What are we storing?"}</h2>
            </div>
            <button className="icon-button" onClick={closeForm} aria-label="Close form"><X /></button>
          </header>

          <form onSubmit={submit} className="item-form">
            <div className="item-form-scroll">
            {!initial && (
              <button className="scan-inline" type="button" onClick={() => setScannerOpen(true)}>
                <span className="scan-inline-icon"><Camera /></span>
                <span><strong>Scan a barcode</strong><small>Use your phone camera to fill details</small></span>
                <Barcode />
              </button>
            )}

            {lookupBusy && <div className="lookup-status"><LoaderCircle className="spin" /> Looking up this product…</div>}
            {lookup && !lookupBusy && (
              <div className={`lookup-status ${lookup.found ? "success" : "neutral"}`}>
                {lookup.found ? <Check /> : <PackagePlus />}
                {lookup.found ? `Found ${lookup.productName}` : "Not found — add the details manually"}
              </div>
            )}

            <div className="form-grid">
              <div className="field span-2 product-history-field">
                <label htmlFor="product-name">Product name</label>
                <div className="product-name-input">
                  <input id="product-name" value={form.productName} onChange={(event) => { setHistorySelection(""); change("productName", event.target.value); }} placeholder="Search saved products or add a new one" autoComplete="off" />
                  {historyBusy && <LoaderCircle className="history-spinner spin" aria-label="Searching saved products" />}
                </div>
                {!historyBusy && historyResults.length > 0 && (
                  <div className="product-history-results" role="listbox" aria-label="Saved household products">
                    <span>Saved household products</span>
                    {historyResults.map((product) => (
                      <button key={product.id} type="button" role="option" onClick={() => selectHistoryProduct(product)}>
                        <span><strong>{product.productName}</strong><small>{product.brand || product.category}</small></span>
                        <em className={product.status.toLowerCase()}>{historyStatusLabels[product.status]}</em>
                      </button>
                    ))}
                  </div>
                )}
                {!historyBusy && !historyError && historyResults.length === 0 && form.productName.trim().length >= 2 && !historySelection && <small className="history-empty">No saved product found — continue adding a new one.</small>}
                {historyError && <small className="history-search-error">{historyError}</small>}
              </div>
              <label className="field">Brand
                <input value={form.brand} onChange={(event) => change("brand", event.target.value)} placeholder="Optional" />
              </label>
              <label className="field">Category
                <select value={form.category} onChange={(event) => change("category", event.target.value)}>
                  {['Dairy', 'Produce', 'Meat', 'Grains', 'Bakery', 'Snacks', 'Drinks', 'Condiments', 'Spices', 'Frozen', 'Other'].map((category) => <option key={category}>{category}</option>)}
                </select>
              </label>
              <div className="field"><label htmlFor="item-quantity">Quantity</label>
                <NumberControl id="item-quantity" label="quantity" value={form.quantity} onChange={(value) => change("quantity", value)} />
              </div>
              <label className="field">Unit
                <select value={form.unit} onChange={(event) => change("unit", event.target.value)}>
                  {['pcs', 'pack', 'bag', 'bottle', 'carton', 'tub', 'kg', 'g', 'L', 'mL'].map((unit) => <option key={unit}>{unit}</option>)}
                </select>
              </label>
              <label className="field span-2">Stored in
                <div className="segment-control">
                  {(["Pantry", "Fridge", "Freezer"] as PantryLocation[]).map((location) => (
                    <button key={location} type="button" className={form.location === location ? "active" : ""} onClick={() => change("location", location)}>{location}</button>
                  ))}
                </div>
              </label>
              <label className="field span-2">Pack status
                <div className="segment-control two-options">
                  <button type="button" className={!form.isOpen ? "active" : ""} aria-pressed={!form.isOpen} onClick={() => change("isOpen", false)}>Sealed</button>
                  <button type="button" className={form.isOpen ? "active" : ""} aria-pressed={form.isOpen} onClick={() => change("isOpen", true)}>Open</button>
                </div>
              </label>
              <label className="field">Expiry date
                <input type="date" value={form.expiryDate} onChange={(event) => change("expiryDate", event.target.value)} />
              </label>
              <label className="field">Date type
                <select value={form.expiryType} onChange={(event) => change("expiryType", event.target.value as InventoryInput["expiryType"])}>
                  <option value="best-before">Best before</option>
                  <option value="use-by">Use by</option>
                </select>
              </label>
              <div className="field"><label htmlFor="low-stock-quantity">Low-stock alert at</label>
                <NumberControl id="low-stock-quantity" label="low-stock alert" value={form.minimumQuantity} onChange={(value) => change("minimumQuantity", value)} />
              </div>
              <label className="field">Barcode
                <input inputMode="numeric" value={form.barcode} onChange={(event) => change("barcode", event.target.value.replace(/\D/g, ""))} placeholder="Optional" />
              </label>
              <label className="field span-2">Notes
                <textarea value={form.notes} onChange={(event) => change("notes", event.target.value)} placeholder="Anything the household should know" rows={2} />
              </label>
            </div>

            {form.imageUrl && <img className="product-preview" src={form.imageUrl} alt="Product preview" />}
            {lookup?.source === "Open Food Facts" && <p className="attribution">Product information from Open Food Facts (ODbL).</p>}
            {error && <p className="form-error" role="alert">{error}</p>}
            </div>

            <div className="sheet-actions">
              <button className="secondary-button" type="button" onClick={closeForm}>Cancel</button>
              <button className="primary-button" type="submit" disabled={saving}>
                {saving ? <LoaderCircle className="spin" /> : <Check />}{saving ? "Saving…" : initial ? "Save changes" : "Add to pantry"}
              </button>
            </div>
          </form>
        </section>
      </div>
      {scannerOpen && <BarcodeScanner onDetected={resolveBarcode} onClose={() => setScannerOpen(false)} />}
    </>
  );
}
